import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar,
  TrendingUp,
  Lightbulb,
  Heart,
  Link2,
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  FileText,
  CheckCircle
} from 'lucide-react';
import { Button, Card } from '../ui';
import { morningPagesService, type WeeklyInsightSummaryDto } from '../../services';
import './WeeklySummaryView.css';

interface WeeklySummaryViewProps {
  onRefresh?: () => void;
  initialWeekStart?: string;
}

export const WeeklySummaryView: React.FC<WeeklySummaryViewProps> = ({
  onRefresh,
  initialWeekStart,
}) => {
  const [summary, setSummary] = useState<WeeklyInsightSummaryDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentWeekStart, setCurrentWeekStart] = useState<string | null>(initialWeekStart || null);

  const loadSummary = useCallback(async (weekStart?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = weekStart
        ? await morningPagesService.getWeeklySummary(weekStart)
        : await morningPagesService.getLatestWeeklySummary();

      setSummary(result);
      if (result) {
        setCurrentWeekStart(result.weekStartDate);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load weekly summary');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary(initialWeekStart);
  }, [loadSummary, initialWeekStart]);

  const handleGenerate = async (forceRegenerate = false) => {
    if (!currentWeekStart) {
      // Default to current week's Monday
      const today = new Date();
      const monday = new Date(today);
      monday.setDate(today.getDate() - today.getDay() + 1);
      setCurrentWeekStart(monday.toISOString().split('T')[0]);
    }

    setIsGenerating(true);
    setError(null);

    try {
      const result = await morningPagesService.generateWeeklySummary({
        weekStartDate: currentWeekStart!,
        forceRegenerate,
      });
      setSummary(result);
      onRefresh?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate summary');
    } finally {
      setIsGenerating(false);
    }
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    if (!currentWeekStart) return;

    const current = new Date(currentWeekStart);
    const offset = direction === 'prev' ? -7 : 7;
    current.setDate(current.getDate() + offset);

    const newWeekStart = current.toISOString().split('T')[0];
    setCurrentWeekStart(newWeekStart);
    loadSummary(newWeekStart);
  };

  const formatDateRange = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

    if (startDate.getMonth() === endDate.getMonth()) {
      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.getDate()}`;
    }

    return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
  };

  if (isLoading) {
    return (
      <Card variant="elevated" padding="lg" className="weekly-summary weekly-summary--loading">
        <Loader2 size={32} className="weekly-summary__spinner" />
        <p>Loading weekly summary...</p>
      </Card>
    );
  }

  if (error && !summary) {
    return (
      <Card variant="elevated" padding="lg" className="weekly-summary weekly-summary--error">
        <p>{error}</p>
        <Button onClick={() => loadSummary(currentWeekStart || undefined)}>
          Try Again
        </Button>
      </Card>
    );
  }

  if (!summary) {
    return (
      <Card variant="elevated" padding="lg" className="weekly-summary weekly-summary--empty">
        <div className="weekly-summary__empty-icon">
          <Calendar size={48} />
        </div>
        <h3>No Weekly Summary Yet</h3>
        <p>Generate your first weekly summary to see personalized insights from your morning pages.</p>
        <Button
          variant="primary"
          onClick={() => handleGenerate(false)}
          loading={isGenerating}
          disabled={isGenerating}
        >
          <Sparkles size={18} />
          Generate Summary
        </Button>
      </Card>
    );
  }

  return (
    <div className="weekly-summary">
      {/* Header with Navigation */}
      <div className="weekly-summary__header">
        <div className="weekly-summary__nav">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateWeek('prev')}
            title="Previous week"
          >
            <ChevronLeft size={20} />
          </Button>
          <div className="weekly-summary__period">
            <Calendar size={16} />
            <span>Week {summary.weekNumber}</span>
            <span className="weekly-summary__date-range">
              {formatDateRange(summary.weekStartDate, summary.weekEndDate)}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateWeek('next')}
            title="Next week"
          >
            <ChevronRight size={20} />
          </Button>
        </div>
        <div className="weekly-summary__actions">
          {summary.isRead && (
            <span className="weekly-summary__read-badge">
              <CheckCircle size={14} />
              Read
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleGenerate(true)}
            disabled={isGenerating}
            title="Regenerate summary"
          >
            <RefreshCw size={16} className={isGenerating ? 'weekly-summary__spinner' : ''} />
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="weekly-summary__stats">
        <div className="weekly-summary__stat">
          <FileText size={16} />
          <span>{summary.morningPagesCount} pages</span>
        </div>
        <div className="weekly-summary__stat">
          <Sparkles size={16} />
          <span>{summary.topThemes.length} themes</span>
        </div>
      </div>

      {/* Top Themes */}
      <Card variant="outlined" padding="md" className="weekly-summary__section">
        <h4 className="weekly-summary__section-title">
          <Sparkles size={18} />
          Top Themes
        </h4>
        <div className="weekly-summary__themes">
          {summary.topThemes.map((theme, index) => (
            <span key={index} className="weekly-summary__theme-tag">
              {theme}
            </span>
          ))}
        </div>
      </Card>

      {/* Emotional Trend */}
      <Card variant="outlined" padding="md" className="weekly-summary__section">
        <h4 className="weekly-summary__section-title">
          <Heart size={18} />
          Emotional Trend
        </h4>
        <p className="weekly-summary__text">{summary.emotionalTrend}</p>
      </Card>

      {/* Key Insights */}
      <Card variant="outlined" padding="md" className="weekly-summary__section">
        <h4 className="weekly-summary__section-title">
          <TrendingUp size={18} />
          Key Insights
        </h4>
        <p className="weekly-summary__text">{summary.keyInsights}</p>
      </Card>

      {/* Recommendations */}
      <Card variant="outlined" padding="md" className="weekly-summary__section weekly-summary__section--highlight">
        <h4 className="weekly-summary__section-title">
          <Lightbulb size={18} />
          Recommendations
        </h4>
        <p className="weekly-summary__text">{summary.recommendations}</p>
      </Card>

      {/* Correlation Insights */}
      {summary.correlationInsights && (
        <Card variant="outlined" padding="md" className="weekly-summary__section">
          <h4 className="weekly-summary__section-title">
            <Link2 size={18} />
            Patterns & Correlations
          </h4>
          <p className="weekly-summary__text">{summary.correlationInsights}</p>
        </Card>
      )}

      {/* Footer */}
      <div className="weekly-summary__footer">
        <span className="weekly-summary__generated">
          Generated {new Date(summary.generatedAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </span>
      </div>
    </div>
  );
};

export default WeeklySummaryView;
